import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import { useToast } from '@/features/ui/toast/useToast';
import { BulkMessageDialog } from '@/features/marketplace-crm/BulkMessageDialog';
import { ContactMessageDialog } from '@/features/marketplace-crm/ContactMessageDialog';
import { CustomerForm, type CustomerFormValues } from '@/features/marketplace-crm/CustomerForm';
import {
  OFFER_STATUS_LABELS,
  OFFER_STATUS_OPTIONS,
  OFFER_TYPE_CONFIG,
  type CrmCustomer,
  type CrmOffer,
  type OfferStatus,
} from '@/features/marketplace-crm/customerTypes';
import {
  IconBuilding,
  IconGift,
  IconMail,
  IconMapPin,
  IconPackage,
  IconPercent,
  IconPhone,
  IconPlus,
  IconSend,
  IconTag,
  IconUsers,
} from '@/features/marketplace-crm/icons';
import { OfferForm, type OfferFormValues } from '@/features/marketplace-crm/OfferForm';
import { syncWonLeadsToCustomers } from '@/features/marketplace-crm/syncWonLeads';
import '@/features/marketplace-crm/marketplace-crm.css';
import '@/features/pilotage/pilotage.css';

function initials(c: CrmCustomer) {
  return `${c.first_name?.[0] ?? ''}${c.last_name?.[0] ?? ''}`.toUpperCase() || '?';
}

function extractOpportunityFromNotes(notes: string | null | undefined) {
  const raw = notes ?? '';
  const m = raw.match(/Opportunit\u00e9\s*CRM\s*:\s*(.+)/i);
  return m?.[1]?.trim() ?? '';
}

function OfferTypeIcon({ type }: { type: string }) {
  if (type === 'promotion') return <IconTag size={16} />;
  if (type === 'remise') return <IconPercent size={16} />;
  return <IconPackage size={16} />;
}

export function FidelisationPage() {
  const { profile } = useAuth();
  const toast = useToast();
  const [customers, setCustomers] = useState<CrmCustomer[]>([]);
  const [offers, setOffers] = useState<CrmOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [showBulkMessage, setShowBulkMessage] = useState(false);
  const [showContactMessage, setShowContactMessage] = useState(false);

  const selected = useMemo(
    () => customers.find((c) => c.id === selectedId) ?? null,
    [customers, selectedId],
  );

  const customerOffers = useMemo(
    () => (selected ? offers.filter((o) => o.customer_id === selected.id) : []),
    [offers, selected],
  );

  const latestOffer = customerOffers[0] ?? null;
  const opportunityFromNotes = extractOpportunityFromNotes(selected?.notes);

  async function load() {
    if (!profile) return;
    const syncErr = await syncWonLeadsToCustomers(profile.id);
    if (syncErr) toast.error(syncErr);

    const [cRes, oRes] = await Promise.all([
      supabase
        .from('crm_customers')
        .select('*')
        .eq('user_id', profile.id)
        .order('created_at', { ascending: false }),
      supabase
        .from('crm_offers')
        .select('*')
        .eq('user_id', profile.id)
        .order('created_at', { ascending: false }),
    ]);

    if (cRes.error) toast.error(cRes.error.message);
    else {
      const list = (cRes.data ?? []) as CrmCustomer[];
      setCustomers(list);
      setSelectedId((prev) => prev ?? list[0]?.id ?? null);
    }

    if (oRes.error) toast.error(oRes.error.message);
    else setOffers((oRes.data ?? []) as CrmOffer[]);

    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, [profile]);

  async function saveCustomer(values: CustomerFormValues) {
    if (!profile) return 'Session expirée.';
    const { data, error: err } = await supabase
      .from('crm_customers')
      .insert({
        user_id: profile.id,
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        phone: values.phone,
        company: values.company,
        address: values.address,
        notes: values.notes,
        purchase_count: values.purchase_count,
        nps_score: values.nps_score === '' ? null : values.nps_score,
        last_purchase_at: values.purchase_count > 0 ? new Date().toISOString() : null,
      })
      .select('*')
      .single();
    if (err) return err.message;
    await load();
    if (data?.id) setSelectedId(String(data.id));
    return null;
  }

  async function saveOffer(values: OfferFormValues) {
    if (!profile || !selected) return 'Sélectionnez un client.';
    const { error: err } = await supabase.from('crm_offers').insert({
      user_id: profile.id,
      customer_id: selected.id,
      title: values.title,
      description: values.description,
      offer_type: values.offer_type,
      status: values.status,
      valid_until: values.valid_until || null,
    });
    if (err) return err.message;
    await load();
    return null;
  }

  async function updateOfferStatus(offerId: string, status: OfferStatus) {
    if (!profile) return;
    const previous = offers.find((o) => o.id === offerId)?.status;
    if (previous === status) return;
    setOffers((list) => list.map((o) => (o.id === offerId ? { ...o, status } : o)));
    const { error: err } = await supabase
      .from('crm_offers')
      .update({ status })
      .eq('id', offerId)
      .eq('user_id', profile.id);
    if (err) {
      setOffers((list) =>
        list.map((o) => (o.id === offerId ? { ...o, status: previous ?? o.status } : o)),
      );
      toast.error(err.message || 'Impossible de mettre à jour le statut');
      return;
    }
    toast.success(`Statut mis à jour : ${OFFER_STATUS_LABELS[status] ?? status}`);
  }

  if (loading) {
    return (
      <div className="u-page mktcrm">
        <div className="crm-loading">
          <div className="crm-spinner" aria-label="Chargement" />
        </div>
      </div>
    );
  }

  return (
    <div className="u-page mktcrm">
      <div className="u-hero">
        <div>
          <p className="u-subtitle">Clients revenus, NPS, base clients et offres</p>
        </div>
        <div className="u-actions cust-hero-actions">
          <Link to="/app/marketplace-crm" className="btn btn-ghost">
            ← Hub Marketplace & CRM
          </Link>
          <button
            type="button"
            className="btn cust-btn-bulk"
            disabled={customers.length === 0}
            onClick={() => setShowBulkMessage(true)}
            title="Envoyer le même message à tous les clients de la base"
          >
            <IconSend size={16} /> Message groupé
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setShowCustomerForm(true)}>
            <IconPlus size={16} /> Ajouter un client
          </button>
        </div>
      </div>

      <div className="pilotage-resource-kpis" style={{ marginBottom: '1rem' }}>
        <div>
          <span className="muted">Clients</span>
          <strong>{customers.length}</strong>
        </div>
        <div>
          <span className="muted">Clients revenus</span>
          <strong>{customers.filter((c) => (c.purchase_count ?? 0) > 1).length}</strong>
        </div>
        <div>
          <span className="muted">NPS moyen</span>
          <strong>
            {(() => {
              const scores = customers.map((c) => c.nps_score).filter((v): v is number => typeof v === 'number');
              return scores.length
                ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1)
                : '—';
            })()}
          </strong>
        </div>
        <div>
          <span className="muted">Offres</span>
          <strong>{offers.length}</strong>
        </div>
      </div>

      <div className="cust-layout">
        <div className="cust-list">
          {customers.length === 0 ? <p className="crm-stage-empty">Aucun client</p> : null}
          {customers.map((c) => {
            const count = offers.filter((o) => o.customer_id === c.id).length;
            const active = selected?.id === c.id;
            return (
              <button
                key={c.id}
                type="button"
                className={`cust-card${active ? ' is-active' : ''}`}
                onClick={() => setSelectedId(c.id)}
              >
                <div className="cust-card-row">
                  <div className="cust-avatar">{initials(c)}</div>
                  <div className="cust-card-meta">
                    <p className="cust-card-name">
                      {c.first_name} {c.last_name}
                    </p>
                    {c.company ? <p className="cust-card-company">{c.company}</p> : null}
                  </div>
                </div>
                <p className="cust-card-offers">{count} offre(s)</p>
              </button>
            );
          })}
        </div>

        <div className="cust-detail">
          {selected ? (
            <div className="cust-detail-panel">
              <div className="cust-detail-head">
                <div className="cust-card-row">
                  <div className="cust-avatar cust-avatar--lg">{initials(selected)}</div>
                  <div>
                    <h3>
                      {selected.first_name} {selected.last_name}
                    </h3>
                    {selected.company ? <p className="muted">{selected.company}</p> : null}
                  </div>
                </div>
                <div className="cust-detail-actions">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={!selected.email?.trim() && !selected.phone?.trim()}
                    onClick={() => setShowContactMessage(true)}
                    title="Envoyer un message uniquement à ce client"
                  >
                    <IconSend size={16} /> Message
                  </button>
                  <button type="button" className="btn btn-primary" onClick={() => setShowOfferForm(true)}>
                    <IconGift size={16} /> Nouvelle offre
                  </button>
                </div>
              </div>

              <div className="cust-info-grid">
                {selected.email ? (
                  <div className="cust-info">
                    <IconMail size={16} /> {selected.email}
                  </div>
                ) : null}
                {selected.phone ? (
                  <div className="cust-info">
                    <IconPhone size={16} /> {selected.phone}
                  </div>
                ) : null}
                {selected.address ? (
                  <div className="cust-info">
                    <IconMapPin size={16} /> {selected.address}
                  </div>
                ) : null}
                {selected.company ? (
                  <div className="cust-info">
                    <IconBuilding size={16} /> {selected.company}
                  </div>
                ) : null}
                <div className="cust-info">
                  <IconUsers size={16} /> {selected.purchase_count ?? 0} achat(s)
                  {(selected.purchase_count ?? 0) > 1 ? ' · client revenu' : ''}
                </div>
                {selected.nps_score != null ? (
                  <div className="cust-info">
                    <IconGift size={16} /> NPS {selected.nps_score}/10
                  </div>
                ) : null}
              </div>

              {selected.notes ? (
                <div className="cust-notes">
                  <p className="cust-notes-label">Notes</p>
                  <p>{selected.notes}</p>
                </div>
              ) : null}

              <h4 className="cust-offers-title">Offres envoyées</h4>
              {customerOffers.length === 0 ? (
                <p className="cust-offers-empty">Aucune offre envoyée pour le moment</p>
              ) : (
                <div className="cust-offers">
                  {customerOffers.map((o) => {
                    const tc = OFFER_TYPE_CONFIG[o.offer_type] ?? OFFER_TYPE_CONFIG.nouveau_produit;
                    return (
                      <article key={o.id} className="cust-offer">
                        <div className="cust-offer-main">
                          <div className={`cust-offer-icon cust-offer-icon--${tc.tone}`}>
                            <OfferTypeIcon type={o.offer_type} />
                          </div>
                          <div>
                            <p className="cust-offer-title">{o.title}</p>
                            {o.description ? <p className="cust-offer-desc">{o.description}</p> : null}
                            <div className="cust-offer-meta">
                              <span>{tc.label}</span>
                              {o.valid_until ? (
                                <span>Valable jusqu’au {new Date(o.valid_until).toLocaleDateString('fr-FR')}</span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <label className="cust-offer-status-wrap">
                          <select
                            className={`cust-offer-status cust-offer-status--${o.status}`}
                            value={o.status}
                            onChange={(e) =>
                              void updateOfferStatus(o.id, e.target.value as OfferStatus)
                            }
                            aria-label={`Statut de l’offre ${o.title}`}
                          >
                            {OFFER_STATUS_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="cust-empty-detail">
              <IconUsers size={48} />
              <p>Sélectionnez un client pour voir ses détails et offres</p>
            </div>
          )}
        </div>
      </div>

      {showBulkMessage ? (
        <BulkMessageDialog customers={customers} onClose={() => setShowBulkMessage(false)} />
      ) : null}
      {showContactMessage && selected ? (
        <ContactMessageDialog
          contact={{
            first_name: selected.first_name,
            last_name: selected.last_name,
            email: selected.email,
            phone: selected.phone,
            company: selected.company,
            location: selected.address,
          }}
          title="Message au client"
          subtitle="Envoi uniquement à ce client — e-mail et téléphone repris de sa fiche"
          contextTitle={latestOffer ? 'Offre proposée' : undefined}
          contextHighlight={latestOffer?.title}
          contextBody={[
            latestOffer?.description?.trim() ? latestOffer.description.trim() : '',
            opportunityFromNotes ? `Opportunité : ${opportunityFromNotes}` : '',
          ]
            .filter(Boolean)
            .join('\n')}
          defaultReply={`Bonjour ${selected.first_name?.trim() || 'Client'},\n\nMerci pour votre confiance. Je vous contacte afin de vous partager une nouvelle offre.\n\n${latestOffer ? `Offre : ${latestOffer.title}\n${latestOffer.description?.trim() ? `${latestOffer.description.trim()}\n` : ''}` : ''}${
            opportunityFromNotes ? `Opportunité : ${opportunityFromNotes}\n` : ''
          }\nJe reste disponible pour toute question.\n\nCordialement`}
          onClose={() => setShowContactMessage(false)}
        />
      ) : null}
      {showCustomerForm ? (
        <CustomerForm
          onClose={() => setShowCustomerForm(false)}
          onSave={async (v) => {
            const err = await saveCustomer(v);
            if (!err) setShowCustomerForm(false);
            return err;
          }}
        />
      ) : null}
      {showOfferForm && selected ? (
        <OfferForm
          customer={selected}
          onClose={() => setShowOfferForm(false)}
          onSave={async (v) => {
            const err = await saveOffer(v);
            if (!err) setShowOfferForm(false);
            return err;
          }}
        />
      ) : null}
    </div>
  );
}
